import { useMemo, useState } from 'react';
import { Bell, CheckCheck, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { NotificationDto } from '@/api/notifications.api';
import { EmptyState } from '@/components/feedback/EmptyState';
import { Button } from '@/components/ui/Button';
import { Card, PageHeader } from '@/components/ui/Page';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from '@/hooks/useNotifications';
import { useAuth } from '@/auth/AuthProvider';

const dateText = (value: string) => new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));

export function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [readFilter, setReadFilter] = useState<'ALL' | 'UNREAD'>('ALL');
  const query = useNotifications({ page: 1, pageSize: 50, channel: 'IN_APP', isRead: readFilter === 'UNREAD' ? false : undefined });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const items = useMemo(() => [...(query.data?.items ?? [])].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)), [query.data?.items]);
  const unreadCount = items.filter((item) => !item.isRead).length;

  const openNotification = async (item: NotificationDto) => {
    await markRead.mutateAsync(item.id);
    if (item.relatedEntityType === 'PURCHASE_ORDER' && item.relatedEntityId) navigate(`${user?.userType === 'VENDOR' ? '/portal' : '/app'}/purchase-orders/${item.relatedEntityId}#conversation`);
  };

  return <>
    <PageHeader className="notification-page-header" title="Notifications" description="Review your in-app alerts and open related work directly." actions={unreadCount > 0 ? <Button variant="secondary" onClick={() => void markAll.mutateAsync()} disabled={markAll.isPending}><CheckCheck /> Mark all as read</Button> : undefined} />
    <div className="notification-page-filter" role="group" aria-label="Filter notifications by read status"><button className={readFilter === 'ALL' ? 'active' : ''} onClick={() => setReadFilter('ALL')}>All</button><button className={readFilter === 'UNREAD' ? 'active' : ''} onClick={() => setReadFilter('UNREAD')}>Unread</button></div>
    <Card className="notification-page-list">
      {query.isLoading && <p className="muted">Loading notifications…</p>}
      {query.isError && <EmptyState title="Notifications unavailable" description="Check the API connection and try again." />}
      {!query.isLoading && !query.isError && items.length === 0 && <EmptyState title="No notifications" description="New workflow and conversation alerts will appear here." />}
      {items.map((item) => <button key={item.id} className={`notification-page-item${item.isRead ? '' : ' is-unread'}`} onClick={() => void openNotification(item)} disabled={markRead.isPending}>
        <span className="notification-item__icon">{item.relatedEntityType === 'PURCHASE_ORDER' ? <MessageSquare /> : <Bell />}</span>
        <span><strong>{item.subject}</strong><p>{item.body}</p><small>{item.relatedEntityType?.replaceAll('_', ' ') || 'IN APP'}</small></span>
        <span><time>{dateText(item.createdAt)}</time>{!item.isRead && <b>Unread</b>}</span>
      </button>)}
    </Card>
  </>;
}
